package com.eladfinish.bible.contest.screens.search.usecases.number

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.domain.catalog.BibleCatalog
import com.eladfinish.bible.contest.domain.catalog.toBookIndex
import com.eladfinish.bible.contest.internet_is_required
import com.eladfinish.bible.contest.network.ApiResult
import com.eladfinish.bible.contest.network.error.utils.isInternetError
import com.eladfinish.bible.contest.screens.search.api.NumberReference
import com.eladfinish.bible.contest.screens.search.data.SearchRepository
import com.eladfinish.bible.contest.screens.search.extensions.withGershaimAndColon
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.screens.search.usecases.BuildSearchResultSourceUseCase
import com.eladfinish.bible.contest.utils.gematriaWord
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import io.github.aakira.napier.Napier
import io.ktor.client.plugins.ClientRequestException
import io.ktor.http.HttpStatusCode
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.toImmutableList

class SearchNumberUseCase(
    private val searchRepository: SearchRepository,
    private val buildSearchResultSourceUseCase: BuildSearchResultSourceUseCase,
    private val parseBackendHighlightedContentUseCase: ParseBackendHighlightedContentUseCase,
) {

    private var number = 0

    suspend operator fun invoke(
        searchQuery: String,
        onError: (Pair<StringDesc, Throwable>) -> Unit,
    ): Result<ImmutableList<VerseUiModel>> {
        number = searchQuery.toInt()

        return when (val result = searchRepository.getNumberResults(number)) {
            is ApiResult.Success -> {
                Result.success(
                    mapNumberReferencesToUiModels(
                        items = result.data.verses,
                    ),
                )
            }

            is ApiResult.ClientError -> {
                handleError(
                    throwable = result.throwable,
                    error = onError,
                )
                Result.failure(result.throwable)
            }

            is ApiResult.Failure -> {
                handleError(
                    throwable = result.cause,
                    error = onError,
                )
                Result.failure(result.cause)
            }
        }
    }

    private val bookOrder: Map<String, Int> =
        BibleCatalog.books
            .mapIndexed { index, bookData -> bookData.hebrew to index }
            .toMap()

    private val numberReferenceComparator =
        compareBy<NumberReference>(
            { bookOrder[it.book] ?: Int.MAX_VALUE },
            // c/v are Hebrew numerals ("לג"), not digits
            { it.chapter.gematriaWord() },
            { it.verse.gematriaWord() },
        )

    private fun mapNumberReferencesToUiModels(
        items: List<NumberReference>,
    ): ImmutableList<VerseUiModel> =
        items
            .sortedWith(numberReferenceComparator)
            .mapIndexed { index, item ->
                val parsedContent = parseBackendHighlightedContentUseCase(item.content)

                VerseUiModel(
                    id = buildId(
                        item = item,
                        index = index,
                    ),
                    text = parsedContent.text,
                    source = buildSearchResultSourceUseCase(
                        bookName = item.book,
                        bookIndex = item.book.toBookIndex(),
                        chapter = item.chapter,
                        verse = item.verse,
                    ),
                    highlightedRanges = parsedContent.highlightedRanges,
                    bookIndex = item.book.toBookIndex(),
                    chapterIndex = item.chapter.gematriaWord(),
                    verse = item.verse,
                )
            }
            .toImmutableList()
            .withGershaimAndColon()

    private fun buildId(
        item: NumberReference,
        index: Int,
    ): String =
        listOf(
            item.book,
            item.chapter,
            item.verse,
            index.toString(),
        ).joinToString(separator = "_")

    private fun handleError(
        throwable: Throwable,
        error: (Pair<StringDesc, Throwable>) -> Unit,
    ) {
        Napier.e(message = "SearchNumber", throwable = throwable)

        if (throwable.isInternetError()) {
            error.invoke(SharedRes.strings.internet_is_required.desc() to throwable)
            return
        }

        when (throwable) {
            is ClientRequestException -> {
                val code = throwable.response.status
                Napier.e("code: $code")

                if (code == HttpStatusCode.NotFound) {
                    "המספר $number לא מופיע בתנ\"ך\nאם זו טעות, אנא שלחו דיווח.".desc()
                        .also { message ->
                            error.invoke(message to throwable)
                        }
                }
            }

            else -> {
                error.invoke(
                    "אירעה שגיאה בחיפוש המספר $number".desc() to throwable,
                )
            }
        }
    }
}
